import { useCallback } from "react";
import { useLocation } from "wouter";
import { Phone, FileText, Truck, Award, FolderOpen, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export const INSURED_TABS = [
  { key: "overview", label: "Overview" },
  { key: "communications", label: "Communications" },
  { key: "policies", label: "Policies" },
  { key: "cois", label: "COIs" },
  { key: "assets", label: "Assets" },
  { key: "documents", label: "Documents" },
] as const;

export type InsuredTabKey = (typeof INSURED_TABS)[number]["key"];

const TAB_ICONS: Partial<Record<InsuredTabKey, LucideIcon>> = {
  communications: Phone,
  policies: FileText,
  cois: Award,
  assets: Truck,
  documents: FolderOpen,
};

export function isInsuredTabKey(value: string): value is InsuredTabKey {
  return INSURED_TABS.some((t) => t.key === value);
}

interface Props {
  active: InsuredTabKey;
}

export function InsuredTabs({ active }: Props) {
  const [location, navigate] = useLocation();

  const onSelect = useCallback(
    (key: InsuredTabKey) => {
      navigate(`${location}?tab=${key}`);
    },
    [location, navigate]
  );

  return (
    <div
      className="sticky top-0 z-10 border-b border-border bg-card"
      role="tablist"
      data-testid="insured-tabs"
    >
      <div className="flex gap-1 px-4 md:px-6 flex-wrap">
        {INSURED_TABS.map((tab) => {
          const isActive = tab.key === active;
          const Icon = TAB_ICONS[tab.key];
          return (
            <button
              key={tab.key}
              id={`tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls="insured-tab-panel"
              onClick={() => onSelect(tab.key)}
              className={cn(
                "inline-flex cursor-pointer items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors",
                isActive
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              data-testid={`tab-${tab.key}`}
            >
              {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
